// =============================================
// Enhanced version with more features
// File: src/hooks/useApiEnhanced.js
// =============================================

import { useState, useEffect, useCallback, useRef } from 'react';
import { message } from 'antd';

export const useApiEnhanced = (apiFunc, options = {}) => {
  const {
    immediate = true,
    onSuccess = null,
    onError = null,
    successMessage = null,
    errorMessage = null,
    deps = []
  } = options;

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(immediate);
  const abortControllerRef = useRef(null);

  const execute = useCallback(async (...args) => {
    // Cancel previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setError(null);
    
    try {
      const result = await apiFunc(...args, {
        signal: abortControllerRef.current.signal
      });
      
      setData(result);
      
      if (successMessage) {
        message.success(successMessage);
      }
      
      if (onSuccess) {
        onSuccess(result);
      }
      
      return result;
    } catch (err) {
      // Don't handle aborted requests
      if (err.name === 'AbortError') {
        console.log('Request was cancelled');
        return;
      }
      
      setError(err);
      
      const defaultErrorMessage = err.response?.data?.message || err.message || 'An error occurred';
      message.error(errorMessage || defaultErrorMessage);
      
      if (onError) {
        onError(err);
      }
      
      throw err;
    } finally {
      setLoading(false);
      abortControllerRef.current = null;
    }
  }, [apiFunc, onSuccess, onError, successMessage, errorMessage]);

  // Cleanup function to cancel request on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Execute on mount if immediate is true
  useEffect(() => {
    if (immediate) {
      execute();
    }
  }, deps);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
  }, []);

  return { 
    data, 
    error, 
    loading, 
    execute,
    setData,
    reset
  };
};